import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import * as React from 'react';
import PaidIcon from '@mui/icons-material/Paid';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Sidecard from '../../components/dashboard/SideCard';
import { useNavigate } from 'react-router-dom';
import TransactionTypeDialog from '../../components/dashboard/TransactionTypeDialog';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { isLoggedIn } from '../../components/authProvider/AuthContext';
import { getProfile } from '../../components/authProvider/tabSession';
import {
  CLEAR_DOCS_RESPONSE,
  CLEAR_FEES_RESPONSE,
  CLEAR_TITLE_RESPONSE,
  REMOVE_TITLE_RESPONSE,
  UPDATE_TRANSACTION_INFO
} from '../../redux-state/types';
import Card from '../../components/dashboard/Card';
import CheckIcon from '@mui/icons-material/Check';
import PrimaryButton from '../../components/common/button/PrimaryButton';

const cards = [
  {
    icon: <img className="rounded-lg" src="Vector.png" alt="Dashboard visual" />,
    path: 'lien-check',
    title: 'Lien & Title Check',
    detail: 'DMV Navigator provides robust support for title and lien processes by ensuring collateral is properly secured and verified. With thorough safety and soundness checks, you can confidently mitigate risk at every step, from lien placement to release.',
  },
  {
    icon: <PaidIcon style={{ fontSize: 40 }} />,
    path: 'fees',
    title: 'Fees',
    detail: 'DMV Navigator takes the mystery out of vehicle fees by compiling all the costs you need to know. No more hidden charges or surprise fees; just clear, accurate, and up-to-date information to keep your vehicle transactions on track.',
  },
  {
    icon: <InsertDriveFileIcon style={{ fontSize: 40 }} />,
    path: 'docs',
    title: 'Docs',
    detail: 'DMV Navigator simplifies the entire paperwork process for vehicle transactions, gathering and pre-filling all required forms in one convenient place. Say goodbye to time-consuming searches and penalties; everything you need is clear, accessible, and always up-to-date.',
  },
];

function Dashboard() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [selectedCard, setSelectedCard] = React.useState(null);
  const [selectedCards, setSelectedCards] = React.useState([]);
  const first_name = getProfile('first_name');

  const dispatch = useDispatch();

  useEffect (() => {
    if(!isLoggedIn()) {
      navigate('/login')
    }
  });

  const handleClose = () => {
    setOpen(false);
    setSelectedCard(null);
  };

  const handleSave = (selectedValue) => {
    dispatch({
      type: UPDATE_TRANSACTION_INFO,
      payload: { key: 'transactionType', value: selectedValue },
    });

    dispatch({
      type: REMOVE_TITLE_RESPONSE,
    })

    if (selectedCards.length > 1) {
      navigate('/multiStepForm', {
        state: {
          selectedCards: selectedCards.map(({ path, title }) => ({ path, title })),
          fromMultiStep: true
        }

      });
    } else if (selectedCard) {
      navigate(`/forms/${selectedCard.path}`);
    }

    handleClose();
  };

  const handleProceed = () => {
    dispatch({
      type: CLEAR_FEES_RESPONSE,
    })

    dispatch({
      type: CLEAR_DOCS_RESPONSE,
    })

    dispatch({
      type: CLEAR_TITLE_RESPONSE,
    })

    const requiresTransactionType = selectedCards.some((card) =>
      ['fees', 'docs'].includes(card.path)
    );

    if (selectedCards.length === 1) {
      const card = selectedCards[0];

      if (requiresTransactionType) {
        setSelectedCard(card);
        setOpen(true);
      } else {
        navigate(`/forms/${card.path}`);
      }

    } else if (selectedCards.length > 1) {
      if (requiresTransactionType) {
        setOpen(true);
      } else {
        navigate('/multiStepForm', {
          state: {
            selectedCards: selectedCards.map(({ path, title }) => ({ path, title })),
            fromMultiStep: true
          }
        });
      }
    }
  };


  const handleCardClick = (card) => {
    setSelectedCards((prev) => {
      const alreadySelected = prev.find((c) => c.path === card.path);
      if (alreadySelected) {
        return prev.filter((c) => c.path !== card.path);
      } else {
        return [...prev, card];
      }
    });
  };

  const handleHelpClick = () => {
    window.open('https://www.buyculsloans.com/contact-us/', '_blank', 'noopener,noreferrer');
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="p-8 pb-6 border-b-4 border-white">
          <div className="flex justify-between">
            <div className="">
              <div className="text-4xl font-bold">
                Hello {first_name}<span className="">👋</span>
              </div>
              <div className="mt-2">
                Great to see you again!
              </div>
            </div>

            <div className="mr-4 cursor-pointer" onClick={handleHelpClick}>
              <div className="flex flex-row px-8 py-2 border border-gray-200 rounded-xl bg-white">
                <div className='mr-2'>
                  <HelpOutlineIcon/>
                </div>
                <div className='text-lg'>
                  Need help?
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-8 mt-4 flex flex-col lg:flex-row w-[100%]">
          <div className="flex flex-col w-[100%] justify-between">
            <div className="flex flex-col">
              <div className="text-xl font-bold mb-2">
                Dashboard
              </div>
              <div className="text-gray-600">
                Your One-Stop Solution for Seamless, Compliant Vehicle Transactions.
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-6 w-[100%]">
              <div
                onClick={() => {
                  if (selectedCards.length === cards.length) {
                    setSelectedCards([]);
                  } else {
                    setSelectedCards(cards);
                  }
                }}
                className={`bg-white relative border px-4 py-4 max-w-sm rounded-xl overflow-hidden h-48 w-80 cursor-pointer
                          transition-shadow duration-300 hover:shadow-md hover:shadow-green-100
                          ${selectedCards.length === cards.length ? 'border-green-800' : 'border-gray-100'}`}
              >
                <div className="absolute top-3 right-3">
                  <div
                    className={`w-6 h-6 rounded-md flex items-center justify-center shadow
                                ${selectedCards.length === cards.length ? 'bg-green-800 text-white' : 'bg-white border border-gray-300'}`}
                  >
                    {selectedCards.length === cards.length && <CheckIcon style={{ fontSize: 16 }} />}
                  </div>
                </div>

                <div className="text-xl font-bold text-gray-900 group-hover:text-green-700">
                  Select All
                </div>

                <div className="text-gray-600 mt-6">
                  Use this option to quickly select all available modules - Fees, Docs, and Lien & Title Check.
                </div>
              </div>

              <div className="flex flex-col flex-wrap lg:flex-row gap-6">
                {cards.map((card, index) => {
                  const isSelected = selectedCards.find((c) => c.path === card.path);
                  return (
                    <Card
                      key={index}
                      {...card}
                      isSelected={!!isSelected}
                      onClick={() => handleCardClick(card)}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <div className="h-[400px] w-80 lg:min-w-96 mt-12">
            <Sidecard
              title={"How to check the fee?"}
              detail={"Checkout the new dashboard view. Pages now load faster."}
              button={"Learn More"}
            />
          </div>

          <TransactionTypeDialog open={open} onSave={handleSave} onClose={handleClose} />
        </div>
      </div>

      <div className="mt-6 bg-white p-4 flex justify-end">
        <PrimaryButton
          text="Continue"
          onClick={handleProceed}
          disabled={selectedCards.length === 0}
        />
      </div>
    </>
  );
}

export default Dashboard;